import React, {useState, useEffect} from "react";
import {
  Label,
  ComboBox,
  ComboBoxItem,
  TextArea,
  Input,
  MultiComboBox,
  MultiComboBoxItem,
  RadioButton
} from "@ui5/webcomponents-react";
import UIModel from "../models/UIModel.json";
import localforage from 'localforage';

const renderField = (label, required, content, key) => (
  <div key={key} style={{ marginBottom: "1.5rem" }}>
    <Label required={required} style={{ display: "block", fontWeight: 500 }}>
      {label}
    </Label>
    {content}
  </div>
);

const LastPage = ({ formData, onFormChange, handlePrevious }) => {
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null); 
  const [locationData, setLocationData] = useState(null); 
  const lastData = formData.lastPageData || {
    foodSecurity: {},
    socialProtection: { I1: [], I2: [] },
    peaceAndOrder: {
      J1: "",
      J2: "",
      J3: "",
      J4: ""
    }
  };

  const fetchProvinces = async () => {
    const cacheProvinceKey = 'location_province';
    try {
      const data = await localforage.getItem(cacheProvinceKey);
      setProvinces(data.data);
    } catch (error) {
      console.error("Failed to fetch provinces:", error);
    }
  };

  const fetchMunicipalities = async (provinceId) => {
    const cacheMunicipalityKey = 'location_municipality';
    try {
      const data = await localforage.getItem(cacheMunicipalityKey);
      const filteredData = Array.isArray(data.data)
      ? data.data.find(item => item.province_id === provinceId)?.municipalities
      : [];
      setMunicipalities(filteredData);
    } catch (error) {
      console.error("Failed to fetch municipalities:", error);
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      const [municipalities, provinces] = await Promise.all([
        localforage.getItem('location_municipality'),
        localforage.getItem('location_province'),
      ]);
      setLocationData({ municipalities, provinces });
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchProvinces();

    if (locationData && locationData.provinces && locationData.municipalities) {
      const prevMunicipalityId = parseInt(lastData.peaceAndOrder.J3);
      const municipalities = locationData.municipalities.data;

      const municipalityContainer = municipalities.find(m =>
        m.municipalities.some(mu => mu.municipality_id === prevMunicipalityId)
      );
      if (!municipalityContainer) return;
      const province_id = municipalityContainer.province_id;
      const province_name = locationData.provinces.data.find(p => province_id === province_id).province_name;
      setMunicipalities(municipalityContainer.municipalities);
      setSelectedProvince(province_name);

      setProvinces(locationData.provinces.data);
    }
  }, [locationData]);

  useEffect(() => {
    if (locationData && locationData.municipalities && lastData.peaceAndOrder.J3) {
      const municipalityId = parseInt(lastData.peaceAndOrder.J3);

      const municipalityContainer = locationData.municipalities.data.find(
        m => m.municipalities.find(
          mu => mu.municipality_id === municipalityId
        )
      );


      const municipality = municipalityContainer.municipalities.find(m => m.municipality_id === municipalityId);
      setSelectedMunicipality(municipality.municipality_name);
      const provinceId = municipality.province_id;
      const province = locationData.provinces.data.find(p => p.province_id === provinceId);
      setSelectedProvince(province.province_name);
    }
  }, [locationData]);

  const handleFieldChange = (section, key, value) => {
    const updated = {
      ...lastData,
      [section]: {
        ...lastData[section],
        [key]: value
      }
    };
    onFormChange("lastPageData", updated);
  };

  return (
    <div style={{ padding: "2rem", maxWidth: "900px", margin: "auto" }}>
      <h2>Step 9: Final Section - Food, Social, and Peace & Order</h2>

      {/* H. Food Security */}
      <h3>Section H: Food Security Information</h3>
      {Object.entries(UIModel.foodSecurityQuestions).map(([key, question]) => (
        renderField(
          question.label,
          false,
          question.options.length > 0 ? (
            <div style={{ display: "flex", gap: "1rem" }}>
              {question.options.map((opt, i) => (
                <RadioButton
                  key={`${key}-opt-${i}`}
                  text={opt}
                  name={`foodSecurity-${key}`}
                  checked={lastData.foodSecurity[key] === opt}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleFieldChange("foodSecurity", key, opt);
                    }
                  }}
                />
              ))}
            </div>
          ) : (
            <Input
              value={lastData.foodSecurity[key] || ""}
              onInput={(e) => handleFieldChange("foodSecurity", key, e.target.value.toUpperCase())}
            />
          ),
          `foodSecurity-${key}`
        )
      ))}

      {/* I. Social Protection */}
      <h3>Section I: Social Protection and Services</h3>
      {["I1", "I2"].map((field) =>
        renderField(
          UIModel.socialProtectionLabels[field],
          false,
          <MultiComboBox
            onSelectionChange={(e) => {
              const selectedItems = e.detail.items.map((item) => item.text);
              handleFieldChange("socialProtection", field, selectedItems);
            }}
          >
            {(UIModel.socialProtectionOptions[field] || []).map((opt, i) => {
              const isSelected = (lastData.socialProtection?.[field] || []).includes(opt);
              return (
                <MultiComboBoxItem
                  key={`${field}-opt-${i}`}
                  text={opt}
                  selected={isSelected}
                />
              );
            })}
          </MultiComboBox>,
          `socialProtection-${field}`
        )
      )}

      {/* J. Peace and Order */}
      <h3>Section J: Peace and Order Information</h3>
      {renderField(
        "J1. May miyembro ba ng pamilya na naging BIKTIMA ng krimen sa nakalipas na isang taon?",
        false,
        <ComboBox
          value={lastData.peaceAndOrder.J1 || "Hindi" }
          onChange={(e) => handleFieldChange("peaceAndOrder", "J1", e.target.value)}
        >
          {UIModel.peaceAndOrderOptions.J1.map((opt, i) => (
            <ComboBoxItem key={`J1-opt-${i}`} text={opt} />
          ))}
        </ComboBox>,
        "peaceAndOrder-J1"
      )}

      {lastData.peaceAndOrder.J1 === "Oo" && (
        <>
          {renderField(
            "J2. Uri ng krimen/karahasan",
            true,
            <Input
              value={lastData.peaceAndOrder.J2}
              onInput={(e) => handleFieldChange("peaceAndOrder", "J2", e.target.value.toUpperCase())}
            />,
            "peaceAndOrder-J2"
          )}
          {/* {renderField(
            "J3. Saang lugar naganap ang krimen?",
            true,
            <Input
              value={lastData.peaceAndOrder.J3}
              onInput={(e) => handleFieldChange("peaceAndOrder", "J3", e.target.value.toUpperCase())}
              placeholder="City/Municipality, Province"
            />,
            "peaceAndOrder-J3"
          )} */}
          {renderField(
            "J3. Saang lugar naganap ang krimen?",
            false,
            <div style={{display: 'flex', flexDirection: "column"}}>  
              <Label style={{ display: "block", fontWeight: 500 }}> 
                J3.1. Province
              </Label>
              <ComboBox
                value={selectedProvince}
                onChange={(e) => {
                  const provinceName = e.target.value;
                  const provinceId = provinces.find(p => p.province_name === provinceName).province_id;
                  setSelectedProvince(provinceName);
                  setSelectedMunicipality('');
                  handleFieldChange("peaceAndOrder", "J3", "");
                  setMunicipalities([]);
                  console.log(provinceName)
                  if (provinceName) fetchMunicipalities(provinceId);
                }}
              >
                {provinces.map((opt, i) => (
                  <ComboBoxItem key={`J3-opt-pro-${i}`} text={opt.province_name}/>
                ))}
              </ComboBox>
            </div>,
            "peaceAndOrder-J3-province"
          )}
          {renderField(
            "J3.2. City/Municipality",
            false,
            <div style={{display: 'flex', flexDirection: "column"}}>  
              <ComboBox
                value={selectedMunicipality}
                onChange={(e) => {
                  const municipalityName = e.target.value;
                  const municipalityId = municipalities.find(m => m.municipality_name === municipalityName).municipality_id;
                  setSelectedMunicipality(municipalityName)
                  handleFieldChange("peaceAndOrder", "J3", municipalityId);
                }}
              >
                {municipalities.map((opt, i) => (
                  <ComboBoxItem key={`J3-opt-${i}`} text={opt.municipality_name} data-id={opt.municipality_id}/>
                ))}
              </ComboBox>
            </div>,
            "peaceAndOrder-J3"
          )}
          {renderField(
            "J4. Sa iyong palagay, gaano kaligtas maglakad sa inyong lugar kapag gabi?",
            false,
          <ComboBox
            value={lastData.peaceAndOrder.J4}
            onChange={(e) => handleFieldChange("peaceAndOrder", "J4", e.target.value)}
          >
            {UIModel.peaceAndOrderOptions.J4.map((opt, i) => (
              <ComboBoxItem key={`J4-opt-${i}`} text={opt} />
            ))}
          </ComboBox>,
          "peaceAndOrder-J4"
          )}
        </>
      )}
    </div>
  );
};

export default LastPage;